/**
 * Profile – Display updaters and body setup
 * Functions that update display elements and sync height/weight inputs
 */

import { calculateAge } from '../../utils';
import {
  MIN_AGE,
  MAX_VALID_AGE,
  MIN_HEIGHT_CM,
  MAX_HEIGHT_CM,
} from '../../config';
import { getZodiacFromBirthDate, ZODIAC_SIGN_LABELS } from '../../options';
import { store } from '../../state';
import { showToast } from '../../main';
import { TOAST_FAILED_SAVE } from '../../utils/app-strings';

export function updateAgeDisplay(): void {
  const birthDateInput = document.getElementById('birth_date') as HTMLInputElement;
  const ageDisplay = document.getElementById('age-display');
  if (!birthDateInput || !ageDisplay) return;

  const age = calculateAge(birthDateInput.value);
  ageDisplay.classList.remove('age-display-error');

  if (age === null || !birthDateInput.value) {
    ageDisplay.textContent = '';
  } else if (age < 0) {
    ageDisplay.innerHTML = '<i class="fas fa-times-circle"></i> Cannot be in the future';
    ageDisplay.classList.add('age-display-error');
  } else if (age < MIN_AGE) {
    ageDisplay.innerHTML = `<i class="fas fa-times-circle"></i> Must be at least ${MIN_AGE}`;
    ageDisplay.classList.add('age-display-error');
  } else if (age > MAX_VALID_AGE) {
    ageDisplay.innerHTML = '<i class="fas fa-times-circle"></i> Invalid date';
    ageDisplay.classList.add('age-display-error');
  } else {
    ageDisplay.textContent = `I am ${age.toFixed(2)} years old.`;
  }

  // Update zodiac hint when birth date changes
  updateZodiacHint();
}

export function updateZodiacHint(): void {
  const birthDateInput = document.getElementById('birth_date') as HTMLInputElement;
  const zodiacHint = document.getElementById('zodiac-hint');
  const zodiacRadioGroup = document.querySelector('input[name="zodiac_sign"]:checked') as HTMLInputElement | null;
  if (!birthDateInput || !zodiacHint) return;

  // Only show hint if no zodiac is currently selected
  if (zodiacRadioGroup && zodiacRadioGroup.value) {
    zodiacHint.textContent = '';
    return;
  }

  if (!birthDateInput.value) {
    zodiacHint.textContent = '';
    return;
  }

  const zodiac = getZodiacFromBirthDate(birthDateInput.value);
  if (zodiac && zodiac !== 'pnta') {
    const zodiacLabel = ZODIAC_SIGN_LABELS[zodiac] || zodiac;
    zodiacHint.textContent = `Based on your birthday: ${zodiacLabel}`;
  } else {
    zodiacHint.textContent = '';
  }
}

export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  // Handle edge case where rounding gives 12 inches
  if (inches >= 12) {
    return { feet: feet + 1, inches: 0 };
  }
  return { feet, inches };
}

export function feetInchesToCm(feet: number, inches: number): number {
  const totalInches = (feet * 12) + inches;
  return Math.round(totalInches * 2.54);
}

export function disablePasswordManagers(): void {
  // Prevent password managers (LastPass, 1Password, etc.) from showing on profile fields
  const forms = document.querySelectorAll('.profile-edit-form');
  forms.forEach(form => {
    form.setAttribute('data-lpignore', 'true');
    form.setAttribute('data-1p-ignore', '');
    form.setAttribute('data-form-type', 'other');
  });
  const inputs = document.querySelectorAll('.profile-edit-form input, .profile-edit-form textarea, .profile-edit-form select');
  inputs.forEach(input => {
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('data-lpignore', 'true');
    input.setAttribute('data-1p-ignore', '');
  });
}

export function setupHeightSync(): void {
  const cmInput = document.getElementById('height_cm') as HTMLInputElement;
  const feetInput = document.getElementById('height_feet') as HTMLInputElement;
  const inchesInput = document.getElementById('height_inches') as HTMLInputElement;

  if (!cmInput || !feetInput || !inchesInput) return;

  // Initialize feet/inches from cm value
  const initialCm = parseInt(cmInput.value);
  if (initialCm) {
    const { feet, inches } = cmToFeetInches(initialCm);
    feetInput.value = feet.toString();
    inchesInput.value = inches.toString();
  }

  // When cm changes, update feet/inches
  cmInput.addEventListener('input', () => {
    const cm = parseInt(cmInput.value);
    if (cm && cm >= MIN_HEIGHT_CM && cm <= MAX_HEIGHT_CM) {
      const { feet, inches } = cmToFeetInches(cm);
      feetInput.value = feet.toString();
      inchesInput.value = inches.toString();
    } else if (!cmInput.value) {
      feetInput.value = '';
      inchesInput.value = '';
    }
  });

  // When feet or inches change, update cm
  const updateCmFromImperial = () => {
    const feet = parseInt(feetInput.value) || 0;
    const inches = parseInt(inchesInput.value) || 0;
    if (feet > 0 || inches > 0) {
      const cm = feetInchesToCm(feet, inches);
      if (cm >= MIN_HEIGHT_CM && cm <= MAX_HEIGHT_CM) {
        cmInput.value = cm.toString();
        // Trigger input event to activate auto-save
        cmInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else if (!feetInput.value && !inchesInput.value) {
      cmInput.value = '';
      cmInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  feetInput.addEventListener('input', updateCmFromImperial);
  inchesInput.addEventListener('input', updateCmFromImperial);
}

export function setupBodyPnta(): void {
  const heightPntaCheckbox = document.getElementById('height_pnta') as HTMLInputElement;
  const weightPntaCheckbox = document.getElementById('weight_pnta') as HTMLInputElement;

  // Height PNTA handler
  heightPntaCheckbox?.addEventListener('change', async () => {
    const isPnta = heightPntaCheckbox.checked;

    // Disable inputs when PNTA
    const heightCm = document.getElementById('height_cm') as HTMLInputElement;
    const heightFeet = document.getElementById('height_feet') as HTMLInputElement;
    const heightInches = document.getElementById('height_inches') as HTMLInputElement;
    if (heightCm) { heightCm.disabled = isPnta; if (isPnta) heightCm.value = ''; }
    if (heightFeet) { heightFeet.disabled = isPnta; if (isPnta) heightFeet.value = ''; }
    if (heightInches) { heightInches.disabled = isPnta; if (isPnta) heightInches.value = ''; }

    // Save to profile_json.body.height_pnta and clear height_cm if PNTA
    try {
      const { profileApi } = await import('../../api');
      const state = store.getState();
      const currentProfileJson = state.user?.profile?.profile_json || {};
      const currentBody = currentProfileJson.body || {};

      const updates: any = {
        profile_json: {
          ...currentProfileJson,
          body: {
            ...currentBody,
            height_pnta: isPnta,
          }
        }
      };

      // Clear height when PNTA is checked
      if (isPnta) {
        updates.height_cm = null;
      }

      await profileApi.update(updates);

      // Update local state
      store.setState({
        user: {
          ...state.user!,
          profile: {
            ...state.user!.profile!,
            height_cm: isPnta ? null : (state.user?.profile?.height_cm ?? null),
            profile_json: updates.profile_json
          }
        }
      });

      // Show indicator
      const indicator = document.getElementById('indicator-height');
      if (indicator) {
        indicator.classList.add('visible');
        setTimeout(() => indicator.classList.remove('visible'), 2000);
      }
    } catch (err) {
      console.error('Failed to save height PNTA:', err);
      showToast(TOAST_FAILED_SAVE, 'error');
    }
  });

  // Weight PNTA handler
  weightPntaCheckbox?.addEventListener('change', async () => {
    const isPnta = weightPntaCheckbox.checked;

    // Disable inputs when PNTA
    const weightKg = document.getElementById('weight_kg') as HTMLInputElement;
    const weightLb = document.getElementById('weight_lb') as HTMLInputElement;
    if (weightKg) { weightKg.disabled = isPnta; if (isPnta) weightKg.value = ''; }
    if (weightLb) { weightLb.disabled = isPnta; if (isPnta) weightLb.value = ''; }

    // Save to profile_json.body.weight_pnta and clear weight_lb if PNTA
    try {
      const { profileApi } = await import('../../api');
      const state = store.getState();
      const currentProfileJson = state.user?.profile?.profile_json || {};
      const currentBody = currentProfileJson.body || {};

      await profileApi.update({
        profile_json: {
          ...currentProfileJson,
          body: {
            ...currentBody,
            weight_pnta: isPnta,
            weight_lb: isPnta ? undefined : currentBody.weight_lb,
          }
        }
      });

      // Update local state
      store.setState({
        user: {
          ...state.user!,
          profile: {
            ...state.user!.profile!,
            profile_json: {
              ...currentProfileJson,
              body: {
                ...currentBody,
                weight_pnta: isPnta,
                weight_lb: isPnta ? undefined : currentBody.weight_lb,
              }
            }
          }
        }
      });

      // Show indicator
      const indicator = document.getElementById('indicator-weight');
      if (indicator) {
        indicator.classList.add('visible');
        setTimeout(() => indicator.classList.remove('visible'), 2000);
      }
    } catch (err) {
      console.error('Failed to save weight PNTA:', err);
      showToast(TOAST_FAILED_SAVE, 'error');
    }
  });
}

export function updateLocationDisplay(): void {
  const locationDisplay = document.getElementById('location-display');
  if (!locationDisplay) return;

  const state = store.getState();
  const profile = state.user?.profile;

  locationDisplay.classList.remove('age-display-error');

  const hasLocation = profile?.city || profile?.state || profile?.country;

  if (!hasLocation) {
    locationDisplay.textContent = '';
  } else if (profile?.latitude != null && profile?.longitude != null) {
    const lat = Number(profile.latitude);
    const lng = Number(profile.longitude);
    locationDisplay.textContent = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } else {
    locationDisplay.innerHTML = '<i class="fas fa-times-circle"></i> Location not found';
    locationDisplay.classList.add('age-display-error');
  }
}
